/**
 * Llamadas a la API de autenticación + tipos compartidos.
 */
import { apiFetch, apiFetchRaw, ApiError } from "./client";

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? "/api") as string;

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: "Bearer";
  access_expires_at: string;
  refresh_expires_at: string;
}

export interface LoginRequest {
  email: string;
  password: string;
  mfa_code?: string | null;
}

export interface PermisoMini {
  codigo: string;
  descripcion: string;
}

export interface RolMini {
  id: number;
  codigo: string;
  nombre: string;
  permisos: PermisoMini[];
}

export interface PersonaDeUsuario {
  id: number;
  tipo_documento: string;
  num_documento: string;
  nombres: string;
  ap_paterno: string;
  ap_materno: string | null;
  nombre_completo: string;
  /** "Apellidos, Nombres" — como se nombra en un documento. */
  nombre_formal: string;
  fecha_nacimiento: string | null;
  sexo: string | null;
  telefono: string | null;
  has_documento: boolean;
}

export interface MiPerfilUpdate {
  tipo_documento?: string | null;
  num_documento?: string | null;
  nombres?: string | null;
  ap_paterno?: string | null;
  ap_materno?: string | null;
  fecha_nacimiento?: string | null;
  sexo?: string | null;
  telefono?: string | null;
}

export interface MeResponse {
  id: number;
  username: string | null;
  /** Apellidos y nombre; cae al email si aún no registró sus datos. */
  nombre_para_mostrar: string;
  email: string;
  tenant_nombre: string | null;
  persona: PersonaDeUsuario | null;
  /** Su ficha de profesional si atiende; null para el personal que no atiende. */
  profesional_id: number | null;
  is_super_admin: boolean;
  mfa_habilitado: boolean;
  activo: boolean;
  ultimo_login: string | null;
  roles: RolMini[];
  permisos: string[];
  /**
   * Módulos contratados por el policlínico.
   *
   * No es lo mismo que un permiso y hacen falta los dos: el permiso dice si
   * ESTA persona puede, la funcionalidad si el módulo EXISTE aquí. Un menú que
   * solo mira permisos le ofrece farmacia a una clínica que no la contrató.
   */
  funcionalidades: string[];
}

export class MfaRequiredError extends Error {
  constructor() {
    super("MFA requerido");
  }
}

export async function login(payload: LoginRequest): Promise<TokenResponse> {
  const response = await apiFetchRaw("/v1/auth/login", {
    method: "POST",
    body: payload,
    auth: false,
  });

  if (response.status === 401) {
    if (response.headers.get("X-MFA-Required") === "true") {
      throw new MfaRequiredError();
    }
    let payload: unknown = null;
    try { payload = await response.json(); } catch { /* ignore */ }
    throw new ApiError(401, payload, "Credenciales inválidas");
  }
  if (!response.ok) {
    let payload: unknown = null;
    try { payload = await response.json(); } catch { /* ignore */ }
    throw new ApiError(response.status, payload);
  }
  return (await response.json()) as TokenResponse;
}

export function getMe(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/v1/me");
}

export function changePassword(payload: {
  current_password: string;
  new_password: string;
}): Promise<void> {
  return apiFetch<void>("/v1/me/password", {
    method: "PUT",
    body: payload,
  });
}

export function logout(): Promise<void> {
  return apiFetch<void>("/v1/auth/logout", { method: "POST" });
}


/** Guarda los datos que el propio usuario puede cambiar de sí mismo. */
export function updateMe(payload: MiPerfilUpdate): Promise<MeResponse> {
  return apiFetch<MeResponse>("/v1/me", { method: "PATCH", body: payload });
}

export async function subirMiDocumento(file: File): Promise<MeResponse> {
  // FormData no pasa por apiFetch: fetch tiene que poner él mismo el
  // Content-Type con el boundary del multipart.
  const { getTokens } = await import("../auth/tokens");
  const tokens = getTokens();
  const form = new FormData();
  form.append("file", file);
  const response = await fetch(`${API_BASE}/v1/me/documento`, {
    method: "POST",
    headers: tokens?.access_token
      ? { Authorization: `Bearer ${tokens.access_token}` }
      : {},
    body: form,
  });
  if (!response.ok) {
    const cuerpo = await response.json().catch(() => null);
    throw new Error(cuerpo?.detail ?? "No se pudo subir el documento.");
  }
  return response.json();
}

export function borrarMiDocumento(): Promise<MeResponse> {
  return apiFetch<MeResponse>("/v1/me/documento", { method: "DELETE" });
}

/** URL del escaneo del documento. Pasa por el backend: es dato sensible. */
export function miDocumentoUrl(): string {
  return `${API_BASE}/v1/me/documento`;
}

// ------------------------------------------- recuperar contraseña ----

/**
 * Pide el enlace para elegir una contraseña nueva.
 *
 * Acepta correo o documento porque la gente recuerda uno u otro, y responde lo
 * mismo exista o no la cuenta: decir "ese correo no está registrado"
 * permitiría averiguar quién se atiende aquí probando direcciones.
 */
export function pedirRecuperacion(identificador: string): Promise<{ mensaje: string }> {
  return apiFetch<{ mensaje: string }>("/v1/auth/recuperar", {
    method: "POST",
    body: { identificador },
    auth: false,
  });
}

export function confirmarRecuperacion(
  token: string,
  password: string,
): Promise<void> {
  return apiFetch<void>("/v1/auth/recuperar/confirmar", {
    method: "POST",
    body: { token, password },
    auth: false,
  });
}
